/**
 * Connects to Discord (or the Mock SDK) once on mount and exposes the connection state.
 * @see docs/01-activity-build-guide.md §3
 */
import { useEffect, useState } from 'react';
import { createActivitySdk, type ActivityContext } from '../sdk/index.js';
import { setAuthToken } from '../auth.js';

export type ActivityState =
  | { status: 'connecting' }
  | { status: 'ready'; ctx: ActivityContext; isMock: boolean }
  | { status: 'error'; message: string };

export function useActivity(): ActivityState {
  const [state, setState] = useState<ActivityState>({ status: 'connecting' });

  useEffect(() => {
    // Standard StrictMode-safe pattern: each effect run owns its own `cancelled` flag and starts a
    // fresh connect. The dev double-invoke just connects twice (harmless for the Mock SDK); only
    // the latest run is allowed to commit state. Do NOT add a ref guard here — under StrictMode it
    // would let the first (cancelled) run win and leave the UI stuck on "connecting".
    let cancelled = false;
    (async () => {
      try {
        const sdk = await createActivitySdk();
        const ctx = await sdk.connect();
        // Make the session token available to the API layer (sent as Bearer on write calls).
        setAuthToken(ctx.accessToken);
        if (!cancelled) setState({ status: 'ready', ctx, isMock: sdk.isMock });
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to connect to Discord.';
          setState({ status: 'error', message });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** A friendly display name from a Discord user. */
export function displayName(ctx: ActivityContext): string {
  return ctx.user.globalName || ctx.user.username;
}
